import { isFideTitleCode } from '@swiss-magic/shared-types';
import type { FidePlayerLookupResponse } from '@swiss-magic/shared-types';

// Story 098 — shared FIDE-ID autofill logic, used by both the director's player
// registration card and the player's own profile. Normalizes both surfaces to a
// common string-valued shape, derives the proposed values from a FIDE record,
// and splits them into silent "fills" (target field empty) vs "conflicts"
// (target field already set to a different value → needs confirmation).

export interface FideAutofillTarget {
  firstName: string;
  lastName: string;
  sex: string; // '' | 'm' | 'f'
  birthYear: string; // '' | year
  chessTitle: string; // '' | FIDE title code
  rating: string; // '' | number
  country: string; // '' | ISO-2
}

export type FideAutofillField = keyof FideAutofillTarget;

export interface FideAutofillConflict {
  field: FideAutofillField;
  current: string;
  proposed: string;
}

export interface FideAutofillResult {
  /** Fields that were empty on the target — apply these silently. */
  fills: Partial<FideAutofillTarget>;
  /** Fields already set to a different value — confirm before overwriting. */
  conflicts: FideAutofillConflict[];
  /** Proposed values for the conflicting fields (apply on "Overwrite"). */
  conflictValues: Partial<FideAutofillTarget>;
}

const FIELD_ORDER: FideAutofillField[] = [
  'firstName',
  'lastName',
  'sex',
  'birthYear',
  'chessTitle',
  'rating',
  'country',
];

/** Split a FIDE "Lastname, Firstname" (or "Lastname Firstname") name. */
export function splitFideName(name: string): { firstName: string; lastName: string } {
  const comma = name.indexOf(',');
  if (comma !== -1) {
    return {
      lastName: name.slice(0, comma).trim(),
      firstName: name.slice(comma + 1).trim(),
    };
  }
  // No comma: treat the first token as the surname, the rest as forename(s).
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return { lastName: parts[0], firstName: '' };
  return { lastName: parts[0], firstName: parts.slice(1).join(' ') };
}

/** Build the full set of proposed target values from a FIDE record. */
function proposedFrom(rec: FidePlayerLookupResponse): FideAutofillTarget {
  const { firstName, lastName } = splitFideName(rec.name);
  return {
    firstName,
    lastName,
    sex: rec.sex ?? '',
    birthYear: rec.birthYear != null ? String(rec.birthYear) : '',
    // Only propose titles we actually model (the 8 OTB codes).
    chessTitle: rec.title && isFideTitleCode(rec.title) ? rec.title : '',
    rating: rec.rating != null ? String(rec.rating) : '',
    country: rec.countryIso2 ?? '',
  };
}

export function computeFideAutofill(
  current: FideAutofillTarget,
  rec: FidePlayerLookupResponse,
): FideAutofillResult {
  const proposed = proposedFrom(rec);
  const fills: Partial<FideAutofillTarget> = {};
  const conflicts: FideAutofillConflict[] = [];
  const conflictValues: Partial<FideAutofillTarget> = {};

  for (const field of FIELD_ORDER) {
    const next = proposed[field];
    if (next === '') continue; // FIDE has nothing for this field — leave as is.
    const curr = (current[field] ?? '').trim();
    if (curr === '') {
      fills[field] = next;
    } else if (curr !== next) {
      conflicts.push({ field, current: curr, proposed: next });
      conflictValues[field] = next;
    }
  }

  return { fills, conflicts, conflictValues };
}
